/**
 * 全局：点击弹窗遮罩层时关闭弹窗。
 * 通过触发遮罩所在弹窗内的关闭/取消按钮实现，兼容 funding / admin / drawer 等各类结构。
 *
 * 退出机制：
 * - `data-no-backdrop-close` 或 `data-backdrop-close="false"`
 * - class 含 `--locked`（如 `admin-modal-backdrop--locked`）
 */

const DISMISS_TEXT = new Set(['取消', '关闭', 'Cancel', 'Close']);

const CLOSE_BUTTON_SELECTOR = [
  '[data-modal-close]',
  'button[aria-label="关闭"]',
  'button[aria-label="Close"]',
  'button[aria-label="关闭预览"]',
  'button.funding-modal__close',
  'button.admin-modal__close',
  'button[class*="__close"]',
].join(',');

let installed = false;
let uninstall = null;

function isExcludedOverlayClass(className) {
  return (
    className.includes('drag-overlay')
    || className.includes('loading-overlay')
    || className.includes('card-overlay')
    || className.includes('media-overlay')
    || className.includes('other-device-overlay')
    || className.includes('table-loading-overlay')
    // SHEIN 通知抽屉：遮罩仅挡操作，不可点关闭（否则会误点「关闭」并看起来像被清空）
    || className.includes('spo-notice-backdrop')
    || className.includes('spo-notice-root')
  );
}

/** @param {string} className */
export function isModalBackdropClassName(className) {
  if (!className || isExcludedOverlayClass(className)) return false;
  if (className.includes('modal-backdrop')) return true;
  if (className.endsWith('__backdrop') || className.endsWith('-backdrop')) return true;
  if (className.endsWith('__modal-overlay') || className.endsWith('-modal-overlay')) return true;
  if (className === 'admin-modal-overlay') return true;
  if (className.endsWith('__drawer-backdrop') || className.endsWith('-drawer-backdrop')) return true;
  if (className.includes('preview-overlay')) return true;
  if (className.endsWith('__overflow-backdrop')) return true;
  return false;
}

/** @param {Element | null} el */
export function isModalBackdropElement(el) {
  if (!el || el.nodeType !== 1) return false;
  for (const name of el.classList) {
    if (isModalBackdropClassName(name)) return true;
  }
  return false;
}

/** @param {Element} el */
export function shouldIgnoreBackdropDismiss(el) {
  if (!el) return true;
  if (el.hasAttribute('data-no-backdrop-close')) return true;
  if (el.getAttribute('data-backdrop-close') === 'false') return true;
  for (const name of el.classList) {
    if (name.includes('--locked')) return true;
  }
  return false;
}

/** @param {Element} backdrop */
export function resolveModalRoot(backdrop) {
  // 预览层常把 role=dialog / aria-modal 挂在遮罩自身（非后代）；
  // querySelector 不含自身，若漏判会落到 document.body，误点下层历史「关闭」。
  if (typeof backdrop.matches === 'function'
    && backdrop.matches('[role="dialog"], [aria-modal="true"]')) {
    return backdrop;
  }
  if (backdrop.querySelector('[role="dialog"], [aria-modal="true"]')) {
    return backdrop;
  }
  return backdrop.parentElement || backdrop;
}

/** @param {Element} backdrop */
export function findModalDismissControl(backdrop) {
  const root = resolveModalRoot(backdrop);
  if (!root) return null;

  const explicit = root.querySelector(CLOSE_BUTTON_SELECTOR);
  if (explicit instanceof HTMLButtonElement && !explicit.disabled) {
    return explicit;
  }
  if (explicit instanceof HTMLElement && explicit.tagName === 'BUTTON' && !explicit.hasAttribute('disabled')) {
    return explicit;
  }

  const buttons = root.querySelectorAll('button');
  for (const button of buttons) {
    if (button.disabled) continue;
    const text = (button.textContent || '').replace(/\s+/g, ' ').trim();
    if (DISMISS_TEXT.has(text)) return button;
  }
  return null;
}

/**
 * @param {MouseEvent} event
 * @returns {boolean} 是否已处理
 */
export function handleModalBackdropPointerDown(event) {
  if (event.defaultPrevented) return false;
  if (event.button != null && event.button !== 0) return false;

  const target = event.target;
  if (!(target instanceof Element)) return false;
  if (!isModalBackdropElement(target)) return false;
  if (shouldIgnoreBackdropDismiss(target)) return false;

  const control = findModalDismissControl(target);
  if (!control) return false;

  event.preventDefault();
  event.stopPropagation();
  if (typeof event.stopImmediatePropagation === 'function') {
    event.stopImmediatePropagation();
  }
  control.click();
  return true;
}

/**
 * @param {Document | null} [doc]
 * @returns {() => void} uninstall
 */
export function installModalBackdropDismiss(doc = typeof document !== 'undefined' ? document : null) {
  if (!doc || installed) {
    return uninstall || (() => {});
  }

  const onClickCapture = (event) => {
    handleModalBackdropPointerDown(event);
  };

  doc.addEventListener('click', onClickCapture, true);
  installed = true;
  uninstall = () => {
    doc.removeEventListener('click', onClickCapture, true);
    installed = false;
    uninstall = null;
  };
  return uninstall;
}
